import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled, { css, keyframes } from 'styled-components'
import Image from 'next/image'

import { H4, H5, P } from '../Headings'
import { CTA } from '../CTA'
import { Hyperlink } from '../Hyperlink'
import { StripeCardElement } from '../Modals/StripeCardElement'
import { colors, fontWeights } from '../../utils'
import { NookEvent } from '../../types'
import { Content as RepeatEventSummaryContent } from '../Event/RepeatEventSummary'
import { Toggler } from '../Inputs/Toggler'
import { FormFieldCheckbox } from '../Inputs/FormFieldCheckbox'

const Container = styled.div`
  width: 425px;
  &.recurring {
    width: 525px;
  }
  .header {
    width: 100%;
    margin-bottom: 21px;
    text-align: center;
  }

  .calculationContainer {
    margin-bottom: 35px;

    & .calculationContent {
      padding: 20px;
      border-radius: 6px;
      background-color: ${colors.washedPurpleOpacity40};

      & .calculationLine {
        display: flex;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 11px;

        &.calculationTotal {
          margin-bottom: 0px;

          & p {
            color: ${colors.purple};
            font-weight: ${fontWeights.semiBold};
          }
        }
      }

      & .seprateLine {
        width: 100%;
        border: 1px solid ${colors.veryDarkGray};
        opacity: 0.2;
        margin-bottom: 11px;
      }
    }
  }

  .buttonContainer {
    width: 100%;
    height: 40px;
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;

    .button {
      &.purchaseButton {
        width: 60%;
        max-width: 211px;
      }

      &.purchaseGoBack {
        color: ${colors.grey};
        font-weight: ${fontWeights.medium};
        background: ${colors.white};
        margin-right: 10px;
      }
    }
  }

  .StripeElement {
    width: 100%;
    height: 45px;
  }

  .cardDetailHeader {
    margin-bottom: 6px;
  }

  .paymentContainer {
    & .footerContainer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;

      & .stripeLogo {
        width: 120px;
        height: 40px;
        margin-right: -5px;
      }

      & .paymentError {
        color: ${colors.red};
        width: calc(100% - 120px);
        padding-top: 5px;
        padding-right: 5px;
      }
    }
  }

  .ticketPrice {
    margin-bottom: 17px;
  }

  & .termsContainer {
    .termsCondition {
      width: 458px;
      text-align: center;
      margin-bottom: 19px;
      color: ${colors.veryDarkGray};
      font-size: 12px;
      line-height: 140%;
      letter-spacing: -0.5px;
    }

    & .termsDetail {
      color: ${colors.grey};
      font-size: 12px;

      & .link {
        color: ${colors.purple};
        text-decoration: none;
      }
    }
  }

  & a {
    text-decoration: underLine;
    color: ${colors.offBlack};
  }

  & .separator {
    border: 1px solid ${colors.lightGrayishBlue};
    margin-top: 33px;
    margin-bottom: 33px;
  }
  .signMeUpTitle {
    font-size: 15px;
    font-weight: ${fontWeights.medium};
    color: ${colors.purple};
    line-height: 140%;
    letter-spacing: -0.5px;
    text-align: left;
    opacity: 0.8;
    margin-bottom: 4px;
  }
  .signMeUpContainer {
    margin-top: 22px;
    display: flex;
    column-gap: 64px;
  }
  .termsOfServiceDescription {
    color: ${colors.veryDarkGrayishBlue};
    font-weight: ${fontWeights.normal};
    font-size: 14px;
    letter-spacing: -0.5px;
  }
`
const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const ProcessingPaymentContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 40px;
  padding-bottom: 16px;
  h4 {
    text-align: center;
    margin-bottom: 8px;
  }
  .description {
    color: ${colors.veryDarkGray};
    text-align: center;
  }
  .termsDetail {
    color: ${colors.grey};

    text-align: center;
  }

  .link {
    color: ${colors.purple};
    text-decoration: none;
  }
  .loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    .spinner {
      animation: ${rotate} 2s linear infinite;
    }
    .processingPaymentLabel {
      font-weight: ${fontWeights.medium};
      font-size: 16px;
      line-height: 140%;
      text-align: center;
      letter-spacing: -0.5px;
      color: ${colors.green2};
    }
  }
`
const ProcessingPayment: React.FC = () => {
  const { t } = useTranslation('common')
  return (
    <ProcessingPaymentContainer>
      <H4>{t(`Get tickets to this event`, `Get tickets to this event`)}</H4>
      <div className="loader">
        <div>
          <Image
            className="spinner"
            width="113"
            height="113"
            // eslint-disable-next-line i18next/no-literal-string
            src="/images/greenSpinner.png"
            // eslint-disable-next-line i18next/no-literal-string
            alt="spinner"
          />
        </div>
        <div className="processingPaymentLabel">
          {t('Processing payment', 'Processing payment')}
        </div>
      </div>
      <P className="detail description">
        <Trans i18nKey="ProcessPaymentModal20220519153059693473530">
          You may request to cancel your ticket for a full refund up to 24 hours
          before event. Cancellations after this period will not be eligible for
          a refund. By purchasing a ticket you are agreeing to these terms.
        </Trans>
      </P>
      <P className="detail termsDetail">
        <Trans i18nKey="ProcessPaymentModal20220519153136528126275">
          For terms, refunds and conditions please{' '}
          <Hyperlink className="link" href="/terms" newTab={true}>
            click here
          </Hyperlink>
        </Trans>
      </P>
    </ProcessingPaymentContainer>
  )
}

const PurchaseForm: React.FC<{
  event: Partial<NookEvent>
  error: string
  onClose: (success: boolean) => void
  purchase: () => Promise<void>
  loading: boolean
  onCardDetailsChanged: () => void
}> = ({ event, error, onClose, purchase, loading, onCardDetailsChanged }) => {
  const { t } = useTranslation('common')
  const { price, title, is_recurring: isRecurrent } = event

  const onPurchase = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    purchase()
  }

  const [isSignUpForSeries, setIsSignUpForSeries] = useState(false)

  const onChangeSignup = useCallback(() => {
    setIsSignUpForSeries((state) => !state)
  }, [])
  return (
    <>
      <H4 className="header">{t(`Purchase a ticket`, `Purchase a ticket`)}</H4>
      {isRecurrent ? (
        <div className="calculationContainer">
          <H5>{t(`Total`, `Total`)}</H5>
          <div className="calculationContent">
            <div className="calculationLine calculationTotal">
              <P className="detail">{t(`1x ticket`, `1x ticket`)}</P>
              <P className="detail">
                {t('currencySign', '$')}
                {price.toFixed(2)} {t(`localCurrency`, `USD`)}
              </P>
            </div>
          </div>
          <div className="signMeUpContainer">
            <div>
              <div className="signMeUpTitle">
                <Trans i18nKey="Sign me up for">
                  Sign me up for {{ title }}
                </Trans>
              </div>
              <P className="detail">
                <Trans i18nKey="stripe-20220414170245461748970">
                  Repeats every{' '}
                  <RepeatEventSummaryContent event={event} showUntil={false} />.
                  {/* You will be automatically charged 24hs before each event from
                  the series. */}
                </Trans>
              </P>
            </div>
            <div className="signMeUpToggler">
              <Toggler
                id="sign-up-checkbox"
                // eslint-disable-next-line i18next/no-literal-string
                ariaDescribedby="Sign me up checkbox"
                onChange={onChangeSignup}
                value={isSignUpForSeries}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="calculationContainer">
          <div className="calculationContent">
            <div className="calculationLine calculationTotal">
              <P className="detail">{t(`Total`, `Total`)}</P>
              <P className="detail">
                {t('currencySign', '$')}
                {price.toFixed(2)} {t(`localCurrency`, `USD`)}
              </P>
            </div>
          </div>
        </div>
      )}
      <div className="paymentContainer">
        <div className="headerContainer">
          <H5 className="cardDetailHeader">
            {t(`Card detail`, `Card detail`)}
          </H5>
        </div>
        <StripeCardElement onChange={onCardDetailsChanged} />
        <div className="footerContainer">
          <P className="detail paymentError">{error}</P>
          <img className="stripeLogo" src="/images/powerByStripe.png"></img>
        </div>
        <div className="termsOfService">
          <FormFieldCheckbox
            title={
              <span className="termsOfServiceDescription">
                <Trans i18nKey="stripe-20220414172527968446616">
                  By clicking &quot;Purchase ticket&quot;, I accept the{' '}
                  <Hyperlink href="/terms" newTab={true}>
                    Terms of Service
                  </Hyperlink>{' '}
                  and have read the{' '}
                  <Hyperlink href="/privacy" newTab={true}>
                    Privacy Policy
                  </Hyperlink>
                  .
                </Trans>
              </span>
            }
          />
        </div>
        <div className="buttonContainer">
          {/* Back Button. */}
          <CTA
            className="button closeButton purchaseGoBack"
            onClick={() => onClose(false)}>
            {t(`Go back`, `Go back`)}
          </CTA>
          {/* Process Button. */}
          <CTA
            className="green button purchaseButton"
            onClick={onPurchase}
            disabled={loading}>
            {loading
              ? t('Please wait', 'Please wait')
              : t('Purchase ticket', 'Purchase ticket')}
          </CTA>
        </div>
      </div>
      <div className="separator"></div>
      <div className="termsContainer">
        <P className="detail termsDetail">
          <Trans i18nKey="ProcessPaymentModal374488143">
            Purchases are securely processed through{' '}
            <Hyperlink
              className="link"
              href="https://stripe.com/"
              newTab={true}>
              Stripe
            </Hyperlink>
            . You may request to cancel your ticket for a full refund (minus
            processing fees) up to 24 hours before event. Cancellations after
            this period will not be eligible for a refund. By purchasing a
            ticket you are agreeing to these terms. For terms, refunds and
            conditions please{' '}
            <Hyperlink className="link" href="/terms" newTab={true}>
              click here
            </Hyperlink>
            .
          </Trans>
        </P>
      </div>
    </>
  )
}

const ShowOnLoading = styled.div<{ $loading: boolean }>`
  display: none;
  ${({ $loading }) =>
    $loading &&
    css`
      display: block;
    `}
`

const HideOnLoading = styled.div<{ $loading: boolean }>`
  display: block;
  ${({ $loading }) =>
    $loading &&
    css`
      display: none;
    `}
`

export const StripeCardPurchaseFormComponent: React.FC<{
  event: Partial<NookEvent>
  error: string
  onClose: (success: boolean) => void
  purchase: () => Promise<void>
  loading: boolean
  onCardDetailsChanged: () => void
}> = (props) => {
  const {
    loading,
    event: { is_recurring: isRecurrent },
  } = props

  // Because of the nature of Stripe integration, we can't unmount PurchaseForm,
  // so we are just hiding it
  return (
    <Container className={`${isRecurrent ? 'recurring' : ''}`}>
      <ShowOnLoading $loading={loading}>
        <ProcessingPayment />
      </ShowOnLoading>
      <HideOnLoading $loading={loading}>
        <PurchaseForm {...props} />
      </HideOnLoading>
    </Container>
  )
}
